import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invitationAPI } from '../services/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    username: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const verifyInvitation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await invitationAPI.verify(token);
      setInvitation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyInvitation();
  }, [verifyInvitation]);

  const validateForm = () => {
    const errors = {};

    if (!form.full_name.trim()) {
      errors.full_name = 'Full name is required';
    } else if (form.full_name.trim().length < 2) {
      errors.full_name = 'Name must be at least 2 characters';
    }

    if (!form.username.trim()) {
      errors.username = 'Username is required';
    } else if (form.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(form.username)) {
      errors.username = 'Username can only contain letters, numbers, dots, underscores, and hyphens';
    }

    if (!form.password) {
      errors.password = 'Password is required';
    } else if (form.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (form.password !== form.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError('');

      await invitationAPI.accept(token, {
        full_name: form.full_name.trim(),
        username: form.username.trim(),
        password: form.password,
        phone: form.phone.trim() || null
      });

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-cyan-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Verifying invitation…</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-cyan-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-8 text-center">
          <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">Invalid Invitation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-cyan-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-8 text-center">
          <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-6 h-6 text-green-500 dark:text-green-400" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">Welcome to the Team!</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Your account has been created successfully.</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">Redirecting to login…</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            Sign In Now
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-cyan-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Compact header */}
        <div className="px-6 pt-6 pb-0">
          <div className="flex flex-col items-center gap-0 mb-4">
            <div className="flex items-baseline">
              <span className="text-3xl font-bold bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 bg-clip-text text-transparent tracking-tight" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                Ospito
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent -ml-0.5 -translate-y-1.5 inline-block" style={{ fontFamily: "'Baloo Bhai 2', sans-serif", fontWeight: 700 }}>
                +
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium tracking-wide text-center">Hospital Management System</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          <div className="border-t border-gray-100 dark:border-gray-700/50 pt-1 text-center">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Complete Your Profile</h2>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Set up your account to get started</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-xs">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Read-only invitation fields */}
          <div>
            <label className="label-rx">Facility</label>
            <input
              type="text"
              value={invitation?.facility_name || ''}
              readOnly
              className="input-rx read-only"
            />
          </div>
          <div>
            <label className="label-rx">Email</label>
            <input
              type="email"
              value={invitation?.email || ''}
              readOnly
              className="input-rx read-only"
            />
          </div>

          {/* Full Name + Username in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-rx label-rx-required">Full Name</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className={`input-rx ${formErrors.full_name ? '!border-red-400' : ''}`}
              />
              {formErrors.full_name && (
                <p className="mt-1 text-[11px] text-red-500">{formErrors.full_name}</p>
              )}
            </div>
            <div>
              <label className="label-rx label-rx-required">Username</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
                className={`input-rx ${formErrors.username ? '!border-red-400' : ''}`}
              />
              {formErrors.username && (
                <p className="mt-1 text-[11px] text-red-500">{formErrors.username}</p>
              )}
            </div>
          </div>

          {/* Password + Confirm in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-rx label-rx-required">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={`input-rx ${formErrors.password ? '!border-red-400' : ''}`}
              />
              {formErrors.password && (
                <p className="mt-1 text-[11px] text-red-500">{formErrors.password}</p>
              )}
            </div>
            <div>
              <label className="label-rx label-rx-required">Confirm</label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                className={`input-rx ${formErrors.confirmPassword ? '!border-red-400' : ''}`}
              />
              {formErrors.confirmPassword && (
                <p className="mt-1 text-[11px] text-red-500">{formErrors.confirmPassword}</p>
              )}
            </div>
          </div>

          {/* Phone + Role in a row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-rx">Phone Number <span className="text-gray-400 normal-case tracking-normal">(Optional)</span></label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-rx"
            />
            </div>
            <div>
              <label className="label-rx">Role</label>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
                <span className="w-1 h-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex-shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600 dark:from-orange-400 dark:to-amber-400">
                  {invitation?.role || ''}
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl text-sm font-semibold hover:from-teal-600 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating Account…
              </>
            ) : (
              <>
                Join Team
                <ArrowRightIcon className="w-4 h-4" />
              </>
            )}
          </button>

          <p className="text-[11px] text-center text-gray-400 dark:text-gray-500">
            By joining, you agree to the facility's policies and terms of use.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitation;
